<template>
  <form @submit.prevent="saveRecipe" class="bg-primary-orange">
    <div class="flex justify-between items-center text-gray-800 z-10 px-6 h-24">
      <button @click="backToRecipes" class="p-2 rounded-full active:bg-orange-400" type="button">
        <svg
          class="stroke-current h-6 w-6"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <button class="p-2 rounded-full active:bg-orange-400" type="submit">
        <svg
          class="stroke-current h-6 w-6"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          >
          <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
          <path d="M17 21v-8H7v8M7 3v5h8" />
        </svg>
      </button>
    </div>
    <div class="flex-1 overflow-y-scroll bg-white rounded-t-xl p-6 text-gray-800">
      <textarea
        id="title-field"
        required
        rows="1"
        class="leading-normal w-full text-3xl font-heading resize-none"
        type="text"
        placeholder="Recipe Title"
        v-model="recipe.title"
      ></textarea>

      <div class="-mx-2 -mb-2 mt-4">
        <label class="uppercase text-sm font-heading">Description</label>
        <textarea
          id="description-field"
          class="leading-normal w-full resize-none p-2 border border-gray-400 rounded-lg"
          placeholder="Recipe Description"
          v-model="recipe.description"
        ></textarea>
      </div>
      <div class="-mx-2 mt-2">
        <label class="uppercase text-sm font-heading">Meal Type</label>
        <div class="relative">
          <select
            v-model="recipe.meal_type"
            class="block appearance-none w-full bg-white border border-gray-400 p-2 pr-8 rounded-lg"
          >
            <template v-for="(type, t) in allMealTypes">
              <option :key="t">{{ type }}</option>
            </template>
          </select>
          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
            <svg class="fill-current h-4 w-4" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
            </svg>
          </div>
        </div>
      </div>
      <div class="-mx-2 mt-2">
        <label class="uppercase text-sm font-heading">Tags</label>
        <input
          class="w-full p-2 border border-gray-400 rounded-lg"
          placeholder="Tag Name"
          v-model="newTag"
        />
        <div class="text-right">
          <button
            @click="addTag"
            type="button"
            class="bg-primary-orange active:bg-orange-500 px-3 py-2 mt-2 rounded-lg tracking-wider"
          >Add Tag</button>
        </div>
        <div class="border border-gray-400 rounded-lg mt-2 overflow-hidden">
          <draggable v-if="recipe.tags.length != 0" v-model="recipe.tags" handle=".handle">
            <transition-group>
              <swipe-out v-for="(tag, i) in recipe.tags" :key="i">
                <slot>
                  <div class="px-2 py-4 flex items-center">
                    <div class="mr-2 p-2 text-gray-500 handle">
                      <svg
                        class="stroke-current h-4 w-4"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20"
                        />
                      </svg>
                    </div>
                    <div class="flex-1">{{ tag }}</div>
                  </div>
                </slot>
                <template v-slot:right="close">
                  <button
                    type="button"
                    class="swipeout-action px-4 bg-red-500 active:bg-red-700 text-white w-full flex items-center justify-center"
                    @click="deleteListItem(tag, close, recipe.tags)"
                  >
                    <svg
                      class="stroke-current h-5 w-5"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"
                      />
                    </svg>
                  </button>
                </template>
              </swipe-out>
            </transition-group>
          </draggable>

          <div v-if="recipe.tags.length == 0" class="flex items-center justify-center py-4">
            <p>No Tags</p>
          </div>
        </div>
      </div>
      <div class="-mx-2 mt-2">
        <div class="w-full">
          <label class="uppercase text-sm font-heading">Prep Time</label>
          <div class="flex">
            <div class="relative">
              <select
                v-model="recipe.prep_time.hr"
                class="block appearance-none bg-white border border-gray-400 p-2 pr-8 rounded-lg"
              >
                <template v-for="(item, i) in hourOptions">
                  <option :key="i" :value="item">{{ item }} h</option>
                </template>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                <svg class="fill-current h-4 w-4" viewBox="0 0 20 20">
                  <path
                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                  />
                </svg>
              </div>
            </div>
            <div class="relative ml-2">
              <select
                v-model="recipe.prep_time.min"
                class="block appearance-none bg-white border border-gray-400 p-2 pr-8 rounded-lg"
              >
                <template v-for="(item, i) in minuteOptions">
                  <option :key="i" :value="item">{{ item }} m</option>
                </template>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                <svg class="fill-current h-4 w-4" viewBox="0 0 20 20">
                  <path
                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="-mx-2 mt-2">
        <div class="w-full">
          <label class="uppercase text-sm font-heading">Cook Time</label>
          <div class="flex">
            <div class="relative">
              <select
                v-model="recipe.cook_time.hr"
                class="block appearance-none bg-white border border-gray-400 p-2 pr-8 rounded-lg"
              >
                <template v-for="(item, i) in hourOptions">
                  <option :key="i" :value="item">{{ item }} h</option>
                </template>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                <svg class="fill-current h-4 w-4" viewBox="0 0 20 20">
                  <path
                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                  />
                </svg>
              </div>
            </div>
            <div class="relative ml-2">
              <select
                v-model="recipe.cook_time.min"
                class="block appearance-none bg-white border border-gray-400 p-2 pr-8 rounded-lg"
              >
                <template v-for="(item, i) in minuteOptions">
                  <option :key="i" :value="item">{{ item }} m</option>
                </template>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                <svg class="fill-current h-4 w-4" viewBox="0 0 20 20">
                  <path
                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="-mx-2 mt-2">
        <label class="uppercase text-sm font-heading">Ingredients</label>
        <div class="flex justify-between">
          <div class="w-1/5">
            <input
              v-model="newIngredient.qty"
              class="w-full p-2 border border-gray-400 rounded-lg"
              placeholder="Qty"
            />
          </div>
          <div class="ml-2 w-1/5">
            <div class="relative">
              <select
                v-model="newIngredient.unit"
                class="w-full block appearance-none bg-white border border-gray-400 p-2 pr-8 rounded-lg"
              >
                <template v-for="(unit, u) in allUnits">
                  <option :key="u">{{ unit }}</option>
                </template>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                <svg class="fill-current h-4 w-4" viewBox="0 0 20 20">
                  <path
                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div class="ml-2 flex-1">
            <input
              v-model="newIngredient.name"
              class="w-full p-2 border border-gray-400 rounded-lg"
              placeholder="Name"
            />
          </div>
        </div>
        <div class="text-right">
          <button
            @click="addIngredient"
            type="button"
            class="bg-primary-orange active:bg-orange-500 px-3 py-2 mt-2 rounded-lg tracking-wider"
          >Add Ingredient</button>
        </div>
        <div class="border border-gray-400 rounded-lg mt-2 overflow-hidden">
          <draggable
            v-if="recipe.ingredients.length != 0"
            v-model="recipe.ingredients"
            handle=".handle"
          >
            <transition-group>
              <swipe-out v-for="(ingredient, i) in recipe.ingredients" :key="i">
                <slot>
                  <div class="px-2 py-4 flex items-center">
                    <div class="mr-2 p-2 text-gray-500 handle">
                      <svg
                        class="stroke-current h-4 w-4"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20"
                        />
                      </svg>
                    </div>
                    <div class="flex-1">
                      <span v-if="ingredient.qty">{{ ingredient.qty }}&nbsp;</span>
                      <span v-if="ingredient.unit">{{ ingredient.unit }}&nbsp;</span>
                      <span>{{ ingredient.name }}</span>
                    </div>
                  </div>
                </slot>
                <template v-slot:right="close">
                  <button
                    type="button"
                    class="swipeout-action px-4 bg-red-500 active:bg-red-700 text-white w-full flex items-center justify-center"
                    @click="deleteListItem(ingredient, close, recipe.ingredients)"
                  >
                    <svg
                      class="stroke-current h-5 w-5"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"
                      />
                    </svg>
                  </button>
                </template>
              </swipe-out>
            </transition-group>
          </draggable>

          <div v-if="recipe.ingredients.length == 0" class="flex items-center justify-center py-4">
            <p>No Ingredients</p>
          </div>
        </div>
      </div>
      <div class="-mx-2 mt-2">
        <label class="uppercase text-sm font-heading">Method</label>
        <div class>
          <textarea
            id="method-step-field"
            class="w-full resize-none p-2 border border-gray-400 rounded-lg"
            placeholder="Method Step"
            rows="2"
            v-model="newStep"
          ></textarea>
          <div class="text-right">
            <button
              @click="addMethodStep"
              type="button"
              class="bg-primary-orange active:bg-orange-500 px-3 py-2 mt-2 rounded-lg tracking-wider"
            >Add Method Step</button>
          </div>
          <div class="border border-gray-400 rounded-lg overflow-hidden mt-2">
            <draggable
              v-if="recipe.method.length != 0"
              v-model="recipe.method"
              @start="methodDrag=true"
              @end="methodDrag=false"
              handle=".handle"
            >
              <transition-group>
                <swipe-out v-for="(method, i) in recipe.method" :key="i">
                  <slot>
                    <div class="px-2 py-4 border-gray-400 last:border-b-0 flex items-center">
                      <div class="mr-2 p-2 text-gray-500 handle">
                        <svg
                          class="stroke-current h-4 w-4"
                          fill="none"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20"
                          />
                        </svg>
                      </div>
                      <div class="flex-1">{{ method }}</div>
                    </div>
                  </slot>
                  <template v-slot:right="close">
                    <button
                      type="button"
                      class="swipeout-action px-4 bg-red-500 active:bg-red-700 text-white w-full flex items-center justify-center"
                      @click="deleteListItem(method, close, recipe.method)"
                    >
                      <svg
                        class="stroke-current h-5 w-5"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"
                        />
                      </svg>
                    </button>
                  </template>
                </swipe-out>
              </transition-group>
            </draggable>
            <div v-if="recipe.method.length == 0" class="flex items-center justify-center py-4">
              <p>No Method Steps</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { SwipeOut } from "vue-swipe-actions";
import draggable from "vuedraggable";
import Notification from "@/components/Notification.vue";

export default {
  name: "create",
  components: {
    SwipeOut,
    draggable,
    // eslint-disable-next-line vue/no-unused-components
    Notification
  },
  data() {
    return {
      mode: "create",
      allMealTypes: [
        "Breakfast",
        "Lunch",
        "Dinner",
        "Snack",
        "Baking",
        "Drink"
      ],
      allUnits: [
        "",
        "g",
        "cup",
        "ml",
        "tsp",
        "tbsp",
        "fl oz",
        "1/2 cup",
        "1/4 cup",
        "1/3 cup",
        "pt",
        "l",
        "lb",
        "oz",
        "mg",
        "kg",
        "clove",
        "pinch",
        "drizze"
      ],
      recipe: {
        title: "",
        tags: [],
        ingredients: [],
        method: [],
        cook_time: {
          hr: "0",
          min: "00"
        },
        prep_time: {
          hr: "0",
          min: "00"
        }
      },
      hourOptions: [],
      minuteOptions: [],
      newTag: "",
      newIngredient: {
        unit: ""
      },
      newStep: "",
      methodDrag: false
    };
  },
  mounted() {
    if (this.$router.currentRoute.name == "edit") {
      this.mode = "edit";
      this.$store.dispatch("setLoading", true);
      this.recipe = {
        ...this.recipe,
        ...this.$_.find(
          this.$store.state.allRecipes,
          r => r.id == this.$router.currentRoute.params.recipeId
        )
      };
      this.$store.dispatch("setLoading", false);
    }
    for (let hr = 0; hr <= 10; hr++) {
      this.hourOptions.push(String(hr));
    }
    for (let min = 0; min <= 60; min += 5) {
      this.minuteOptions.push(String(min).padStart(2, "0"));
    }
  },
  watch: {
    "recipe.title": function() {
      let element = document.getElementById("title-field");
      element.style.height = "inherit";
      this.$nextTick(() => {
        element.style.height = element.scrollHeight + "px";
      });
    },
    "recipe.description": function() {
      let element = document.getElementById("description-field");
      let computed = window.getComputedStyle(element);
      element.style.height = "inherit";
      this.$nextTick(() => {
        element.style.height =
          parseInt(computed.getPropertyValue("border-top-width")) +
          parseInt(computed.getPropertyValue("border-bottom-width")) +
          element.scrollHeight +
          "px";
      });
    },
    newStep: function() {
      let element = document.getElementById("method-step-field");
      let computed = window.getComputedStyle(element);
      element.style.height = "inherit";
      element.style.height =
        parseInt(computed.getPropertyValue("border-top-width")) +
        parseInt(computed.getPropertyValue("border-bottom-width")) +
        element.scrollHeight +
        "px";
    }
  },
  methods: {
    backToRecipes: function() {
      if (this.mode == "edit") {
        this.$router.push({
          name: "recipe",
          params: { recipeId: this.recipe.id }
        });
      } else {
        this.$router.replace("/");
      }
    },
    saveRecipe: async function() {
      this.$store.dispatch("setLoading", true);
      this.recipe.updated_at = new Date().toISOString();
      if (this.mode == "edit") {
        try {
          await this.FireService.updateRecipe(this.recipe);
          this.$breadstick.notify(
            ({ h, onClose }) => {
              return h(
                Notification,
                {
                  props: {
                    close: onClose
                  }
                },
                "Recipe saved!"
              );
            },
            {
              position: "bottom"
            }
          );
          this.$router.push({
            name: "recipe",
            params: { recipeId: this.recipe.id }
          });
        } catch (error) {
          console.error("Error updating document: ", error);
          this.$breadstick.notify(
            ({ h, onClose }) => {
              return h(
                Notification,
                {
                  props: {
                    type: "danger",
                    close: onClose
                  }
                },
                error.message
              );
            },
            {
              position: "bottom"
            }
          );
        }
      } else {
        try {
          let recipeId = await this.FireService.createRecipe(this.recipe);
          this.$breadstick.notify(
            ({ h, onClose }) => {
              return h(
                Notification,
                {
                  props: {
                    close: onClose
                  }
                },
                "New recipe created!"
              );
            },
            {
              position: "bottom"
            }
          );
          this.$router.push({
            name: "recipe",
            params: { recipeId: recipeId }
          });
        } catch (error) {
          console.error("Error updating document: ", error);
          this.$breadstick.notify(
            ({ h, onClose }) => {
              return h(
                Notification,
                {
                  props: {
                    type: "danger",
                    close: onClose
                  }
                },
                error.message
              );
            },
            {
              position: "bottom"
            }
          );
        }
      }
      this.$store.dispatch("setLoading", false);
    },
    addIngredient: function() {
      this.recipe.ingredients.push({
        ...this.newIngredient
      });
      this.newIngredient = {
        unit: ""
      };
      this.$forceUpdate();
    },
    addMethodStep: function() {
      this.recipe.method.push(this.newStep);
      this.newStep = "";
    },
    addTag: function() {
      this.recipe.tags.push(this.newTag);
      this.newTag = "";
    },
    deleteListItem: function(tag, close, list) {
      close.close();
      this.$_.pull(list, tag);
      this.$forceUpdate();
    }
  }
};
</script>